/**
 * The arithmetic of a week, as pure functions.
 *
 * The same arrangement `views/desk/viewport.ts` has, and for the same reason: everything here is a
 * calculation with no screen in it, so it can be tested with numbers instead of with a rendered
 * grid — and the grid itself is left with nothing to get wrong.
 *
 * **No date library.** `Intl.DateTimeFormat` with an explicit `timeZone` does exactly what is needed
 * here, and it is the one thing in the browser that is right about summer time without a table being
 * shipped for it. What is *not* used is the plain `Date` getters: they answer in the reader's own
 * zone, and an appointment is drawn in the zone it was spoken in.
 */

/** How many minutes past midnight [at] falls on, in [zone]. */
export function minutesInDay(at: Date, zone: string): number {
  const parts = partsOf(at, zone)
  return Number(parts.hour) * 60 + Number(parts.minute)
}

/** The day [at] falls on in [zone], as `YYYY-MM-DD` — the key a column is looked up by. */
export function dayKey(at: Date, zone: string): string {
  const parts = partsOf(at, zone)
  return `${parts.year}-${parts.month}-${parts.day}`
}

/** Today in the reader's own zone, as `YYYY-MM-DD`. */
export function todayKey(now: Date = new Date()): string {
  return dayKey(now, Intl.DateTimeFormat().resolvedOptions().timeZone)
}

/**
 * The Monday of the week [day] lies in, as `YYYY-MM-DD`.
 *
 * Monday and not Sunday: MyLib is used in Germany, where a week begins on Monday, and a grid whose
 * first column is not the first day of the week is a grid people read wrong all term.
 */
export function weekOf(day: string): string {
  const at = middayOf(day)
  // getUTCDay: 0 is Sunday. Monday is what a week starts on here, so Sunday counts as the seventh.
  const weekday = (at.getUTCDay() + 6) % 7
  at.setUTCDate(at.getUTCDate() - weekday)
  return keyOfUtc(at)
}

/** The seven days of the week starting at [monday], as `YYYY-MM-DD`. */
export function daysOf(monday: string): string[] {
  const at = middayOf(monday)
  return Array.from({ length: 7 }, (_, step) => {
    const day = new Date(at)
    day.setUTCDate(at.getUTCDate() + step)
    return keyOfUtc(day)
  })
}

/** The week [weeks] on from [monday] — negative goes back. */
export function weekFrom(monday: string, weeks: number): string {
  const at = middayOf(monday)
  at.setUTCDate(at.getUTCDate() + weeks * 7)
  return weekOf(keyOfUtc(at))
}

/** The day [days] on from [day] — negative goes back. */
export function dayFrom(day: string, days: number): string {
  const at = middayOf(day)
  at.setUTCDate(at.getUTCDate() + days)
  return keyOfUtc(at)
}

/** A day as somebody reads it: `Mo 17 Aug`, in whatever the browser's own conventions are. */
export function readableDay(day: string): string {
  return middayOf(day).toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
}

/** The day of the month, as the head of a column shows it: `17`. */
export function dayNumber(day: string): string {
  return String(Number(day.slice(8, 10)))
}

/** The weekday, short: `Mon`. */
export function weekdayOf(day: string): string {
  return middayOf(day).toLocaleDateString(undefined, { weekday: 'short', timeZone: 'UTC' })
}

/** A week as somebody reads it: `17 – 23 Aug 2026`. */
export function readableWeek(monday: string): string {
  const days = daysOf(monday)
  const first = middayOf(days[0] as string)
  const last = middayOf(days[6] as string)
  const start = first.toLocaleDateString(undefined, { day: 'numeric', timeZone: 'UTC' })
  const end = last.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
  return `${start} – ${end}`
}

/** A time of day as somebody reads it, in the zone the appointment was spoken in. */
export function readableTime(at: Date, zone: string): string {
  const parts = partsOf(at, zone)
  return `${parts.hour}:${parts.minute}`
}

/** One thing laid out in the grid: where it starts, how long it is, and which track it is on. */
export interface Laid<T> {
  readonly what: T
  /** Minutes past midnight where it begins, in its own zone. */
  readonly from: number
  /** Minutes past midnight where it ends. Never less than [from] plus [SHORTEST]. */
  readonly to: number
  /** Which of [tracks] it sits on, counted from zero. */
  readonly track: number
  /** How many tracks this group of overlapping things needs altogether. */
  readonly tracks: number
}

/** The shortest an appointment may be drawn, so that a five-minute entry is still something to tap. */
export const SHORTEST = 30

/**
 * Lays out one day's worth of things so that nothing covers anything else.
 *
 * Two that overlap in time stand side by side, and the width they share is decided per **group** of
 * mutually overlapping entries rather than for the whole day — otherwise one double-booked morning
 * would make every appointment in the day half as wide.
 *
 * The input does not have to be sorted; the output is, by when things start.
 */
export function overlap<T>(
  things: readonly T[],
  spanOf: (what: T) => { from: number; to: number },
): Laid<T>[] {
  const spans = things
    .map((what) => {
      const span = spanOf(what)
      return { what, from: span.from, to: Math.max(span.to, span.from + SHORTEST) }
    })
    .sort((one, other) => one.from - other.from || one.to - other.to)

  const laid: Laid<T>[] = []
  let group: typeof spans = []
  let groupEnds = -1

  const closeGroup = () => {
    if (group.length === 0) {
      return
    }
    const tracks: number[] = []
    const placed = group.map((span) => {
      let track = tracks.findIndex((endsAt) => endsAt <= span.from)
      if (track === -1) {
        track = tracks.length
        tracks.push(span.to)
      } else {
        tracks[track] = span.to
      }
      return { span, track }
    })
    placed.forEach(({ span, track }) => {
      laid.push({ what: span.what, from: span.from, to: span.to, track, tracks: tracks.length })
    })
    group = []
    groupEnds = -1
  }

  spans.forEach((span) => {
    if (group.length > 0 && span.from >= groupEnds) {
      closeGroup()
    }
    group.push(span)
    groupEnds = Math.max(groupEnds, span.to)
  })
  closeGroup()

  return laid.sort((one, other) => one.from - other.from || one.track - other.track)
}

/** How long a day is, in minutes. The grid shows all of it and scrolls; nothing is cut off. */
export const DAY_MINUTES = 24 * 60

/**
 * The stretch a teaching day happens in.
 *
 * It decides two things and neither of them is a limit: where the grid is scrolled to when it opens,
 * and which part of the column is drawn as daytime. The hours outside it are still there, still
 * reachable and still draggable — an evening study group is an ordinary thing, and a grid that
 * simply had no room for it would be a grid people stop trusting.
 */
export const WORKING_FROM = 7 * 60

export const WORKING_UNTIL = 20 * 60

/**
 * What a drag snaps to, in minutes.
 *
 * A quarter of an hour: fine enough for the quarter-past starts a timetable actually has, coarse
 * enough that nobody has to hit a pixel. Anything more precise is typed rather than dragged.
 */
export const SNAP = 15

/** How long an appointment is when it was clicked rather than dragged. A lecture, in other words. */
export const DEFAULT_LENGTH = 90

/** [minute], snapped to the nearest [step] and kept inside the day. */
export function snapped(minute: number, step: number = SNAP): number {
  const wanted = Math.round(minute / step) * step
  return Math.min(DAY_MINUTES, Math.max(0, wanted))
}

/**
 * Which minute of the day a pointer at [offset] pixels down a column is over.
 *
 * The one piece of arithmetic the dragging rests on, and it is here rather than in the component for
 * the reason everything else in this file is: it can be checked with numbers.
 */
export function minuteAt(offset: number, hourHeight: number): number {
  if (hourHeight <= 0) {
    return 0
  }
  return Math.min(DAY_MINUTES, Math.max(0, Math.round((offset / hourHeight) * 60)))
}

/**
 * The moment at which [minute] of [day] falls, read in [zone].
 *
 * The inverse of {@link minutesInDay}, and the awkward direction: a zone turns a moment into a wall
 * clock easily and the other way round not at all. It is done by guessing UTC and correcting by
 * whatever the zone shifts that moment — twice, because the offset at the corrected moment may
 * differ from the offset at the guess, which is exactly what happens on the night a clock goes
 * forward.
 */
export function momentAt(day: string, minute: number, zone: string): Date {
  const hour = String(Math.floor(minute / 60)).padStart(2, '0')
  const rest = String(minute % 60).padStart(2, '0')
  const guess = new Date(`${day}T${hour}:${rest}:00Z`)
  if (Number.isNaN(guess.getTime())) {
    return guess
  }
  const once = new Date(guess.getTime() - offsetOf(guess, zone))
  return new Date(guess.getTime() - offsetOf(once, zone))
}

/** How far [zone] is from UTC at [at], in milliseconds. */
export function offsetOf(at: Date, zone: string): number {
  const asUtc = new Date(at.toLocaleString('en-US', { timeZone: 'UTC' }))
  const asZone = new Date(at.toLocaleString('en-US', { timeZone: safe(zone) }))
  return asZone.getTime() - asUtc.getTime()
}

/** An ISO moment as the value a `datetime-local` field speaks, in the appointment's own zone. */
export function localValue(iso: string, zone: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) {
    return ''
  }
  const parts = partsOf(at, zone)
  return `${parts.year ?? ''}-${parts.month ?? ''}-${parts.day ?? ''}T${parts.hour ?? ''}:${parts.minute ?? ''}`
}

/** The value of a `datetime-local` field back into an ISO moment, read in [zone]. */
export function momentOfLocal(local: string, zone: string): string | null {
  const [day, time] = local.split('T')
  if (day === undefined || time === undefined) {
    return null
  }
  const [hour, minute] = time.split(':').map(Number)
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return null
  }
  const at = momentAt(day, (hour as number) * 60 + (minute as number), zone)
  return Number.isNaN(at.getTime()) ? null : at.toISOString()
}

/** `HH:MM` as minutes past midnight — what a time field says, as the grid counts it. */
export function minutesOf(time: string): number {
  const [hour, minute] = time.split(':').map(Number)
  if (Number.isNaN(hour) || Number.isNaN(minute)) {
    return 0
  }
  return (hour as number) * 60 + (minute as number)
}

/** The zone the reader is standing in — what an appointment made here is spoken in. */
export function hereZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

/**
 * The two moments a week spans, as the service wants them.
 *
 * A day wider at each end than the week itself, deliberately: the service is asked in UTC and the
 * appointments are drawn in their own zones, so a Monday-morning lecture in a zone ahead of UTC would
 * otherwise fall outside the question. What lands in which column is decided by {@link dayKey}
 * afterwards, and a handful of extra rows cost nothing.
 */
export function weekSpan(monday: string): { from: Date; until: Date } {
  return { from: middayOf(dayFrom(monday, -1), 0), until: middayOf(dayFrom(monday, 8), 0) }
}

/** The two moments a day spans, with the same day of margin and for the same reason. */
export function daySpan(day: string): { from: Date; until: Date } {
  return { from: middayOf(dayFrom(day, -1), 0), until: middayOf(dayFrom(day, 2), 0) }
}

/**
 * A `YYYY-MM-DD` as a moment at midday UTC.
 *
 * Midday and not midnight, on purpose: every arithmetic here shifts whole days, and midnight is the
 * one hour of the day that a summer-time change can move onto the day before.
 */
function middayOf(day: string, hour = 12): Date {
  const parts = day.split('-').map(Number)
  const [year, month, date] = parts
  if (parts.length !== 3 || parts.some(Number.isNaN)) {
    return new Date(Number.NaN)
  }
  return new Date(Date.UTC(year as number, (month as number) - 1, date as number, hour, 0, 0))
}

function keyOfUtc(at: Date): string {
  const month = String(at.getUTCMonth() + 1).padStart(2, '0')
  const day = String(at.getUTCDate()).padStart(2, '0')
  return `${String(at.getUTCFullYear())}-${month}-${day}`
}

/**
 * The parts of [at] in [zone], as strings.
 *
 * `formatToParts` with an explicit `timeZone` is the one way to ask "what does a clock in Berlin say
 * at this moment" without shipping a table of summer-time rules.
 */
function partsOf(at: Date, zone: string): Record<string, string> {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: safe(zone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(at)
  const found: Record<string, string> = {}
  parts.forEach((part) => {
    found[part.type] = part.value
  })
  return found
}

/** A zone the browser knows, or UTC — an appointment written by an older machine still has to draw. */
function safe(zone: string): string {
  try {
    new Intl.DateTimeFormat('en-CA', { timeZone: zone }).format(new Date())
    return zone
  } catch {
    return 'UTC'
  }
}
